/**
 * Hydration and lookup of personal workspace projects and bundles. API records pass through shared
 * snake_case-to-UI converters before entering the in-memory store. Also owns the two bundle
 * mutations other components share: writing an imported bundle's required purpose and merging two
 * bundles. The rules behind both live in Rust (`fluxability_domain::workspace`); this store only
 * calls the API and applies its answer.
 *
 * @module apps/web/src/stores/workspaceStore
 */

import { create } from 'zustand';
import {
  bundleToUI,
  projectToUI,
  type WorkspaceBundleUI,
  type WorkspaceData,
  type WorkspaceProjectUI,
} from '@fluxability/types';
import { api, type BundleMergeResult } from '@/lib/api';
import { useTasksStore } from '@/stores/tasksStore';

/** Outcome of a store mutation: success with a value, or the server's message for the person. */
export type WorkspaceMutationResult<T = undefined> =
  | {
      /** The server accepted the change. */
      ok: true;
      /** Mutation-specific result. */
      value: T;
    }
  | {
      /** The server refused the change or could not be reached. */
      ok: false;
      /** Message to show the person. */
      error: string;
    };

/** Personal project/bundle projection and ID lookups. isLoaded records a successful hydration, not current connectivity; unsuccessful refreshes preserve the prior in-memory workspace. */
interface WorkspaceState {
  /** Normalized personal workspace projects currently loaded in memory. */
  projects: WorkspaceProjectUI[];
  /** Normalized work bundles currently loaded in memory. */
  bundles: WorkspaceBundleUI[];
  /** Server-reported default bundle ID, or null. */
  defaultBundleId: string | null;
  /** Whether a successful workspace payload has been applied; does not indicate freshness. */
  isLoaded: boolean;
  /** Convert wire projects/bundles to UI records and replace the workspace projection. */
  setWorkspace: (data: Partial<WorkspaceData> | null | undefined) => void;
  /** Fetch the workspace and apply successful data; a failure envelope leaves the prior projection intact. */
  loadWorkspace: () => Promise<void>;
  /** Find a loaded bundle by ID; missing or unknown IDs return undefined. */
  getBundle: (id?: string | null) => WorkspaceBundleUI | undefined;
  /** Find a loaded project by ID; missing or unknown IDs return undefined. */
  getProject: (id?: string | null) => WorkspaceProjectUI | undefined;
  /**
   * Save a bundle's purpose and reload the workspace. An empty purpose for an imported bundle is
   * refused by the server (`BUNDLE_PURPOSE_REQUIRED`) and returned as an error.
   */
  setBundlePurpose: (id: string, purpose: string) => Promise<WorkspaceMutationResult>;
  /**
   * Merge bundle `id` into `intoBundleId`, apply the returned workspace, and repoint the moved
   * activities in the task store so open views update without a reload.
   */
  mergeBundle: (
    id: string,
    intoBundleId: string
  ) => Promise<WorkspaceMutationResult<BundleMergeResult>>;
}

/** Turn a thrown request failure into a message, falling back to `fallback`. */
function failureMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Load projects, bundles, and the default bundle from the workspace API, updating state on
 * successful responses. Exposes ID lookups used by task cards and editors; data is not persisted to
 * browser storage.
 */
export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  projects: [],
  bundles: [],
  defaultBundleId: null,
  isLoaded: false,

  setWorkspace: data => {
    const previous = get();
    const projects = reuseEqual(previous.projects, (data?.projects || []).map(projectToUI));
    const bundles = reuseEqual(previous.bundles, (data?.bundles || []).map(bundleToUI));
    set({
      projects,
      bundles,
      defaultBundleId: data?.default_bundle_id || null,
      isLoaded: true,
    });
  },

  loadWorkspace: async () => {
    const response = await api.workspace.get();
    if (response.success && response.data) {
      get().setWorkspace(response.data);
    }
  },

  getBundle: id => get().bundles.find(bundle => bundle.id === id),
  getProject: id => get().projects.find(project => project.id === id),

  setBundlePurpose: async (id, purpose) => {
    try {
      const response = await api.workspace.updateBundle(id, { purpose: purpose.trim() });
      if (!response.success) {
        return { ok: false, error: response.error?.message || 'The purpose was not saved.' };
      }
      await get().loadWorkspace();
      return { ok: true, value: undefined };
    } catch (error) {
      return { ok: false, error: failureMessage(error, 'The purpose was not saved.') };
    }
  },

  mergeBundle: async (id, intoBundleId) => {
    try {
      const response = await api.workspace.mergeBundle(id, intoBundleId);
      if (!response.success || !response.data) {
        return { ok: false, error: response.error?.message || 'The bundles were not merged.' };
      }
      const { merge, workspace } = response.data;
      get().setWorkspace(workspace);
      const projectId = merge.target_bundle.project_id ?? null;
      const tasks = useTasksStore.getState();
      for (const taskId of merge.moved_activity_ids) {
        if (tasks.getTask(taskId)) tasks.updateTask(taskId, { bundleId: intoBundleId, projectId });
      }
      return { ok: true, value: merge };
    } catch (error) {
      return { ok: false, error: failureMessage(error, 'The bundles were not merged.') };
    }
  },
}));

/**
 * Return `previous` when `next` has the same records in the same order, and otherwise reuse each
 * unchanged record object. Activity cards select their bundle and project from this store; an
 * editor that refreshes an unchanged workspace must not re-render every card on the board.
 * Records are small (a few fields each), so the JSON comparison costs microseconds.
 */
function reuseEqual<T extends { /** Record identifier. */ id: string }>(
  previous: T[],
  next: T[]
): T[] {
  const byId = new Map(previous.map(record => [record.id, record]));
  let changed = previous.length !== next.length;
  const merged = next.map((record, index) => {
    const old = byId.get(record.id);
    if (old && JSON.stringify(old) === JSON.stringify(record)) {
      if (previous[index] !== old) changed = true;
      return old;
    }
    changed = true;
    return record;
  });
  return changed ? merged : previous;
}

/**
 * Active bundles that an importer created and that still have no purpose. Call it inside `useMemo`
 * over the store's `bundles`; it returns a new array each time.
 */
export function bundlesNeedingPurpose(bundles: WorkspaceBundleUI[]): WorkspaceBundleUI[] {
  return bundles.filter(bundle => bundle.status === 'active' && bundle.needsPurpose);
}
